import type { ReactElement, ReactNode } from 'react';
import { EmberMascot } from './EmberMascot';
import './SectionEmpty.css';

/**
 * Which quiet mark sits above the copy. One per kind of thing that is missing,
 * so the same absence always draws the same icon.
 */
export type SectionEmptyIcon = 'question' | 'people' | 'search' | 'ember';

const ICONS: Record<SectionEmptyIcon, ReactElement> = {
  /* A question inside a speech bubble: no question has arrived here yet. */
  question: (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.5 11.3c0 3.9-3.8 7.1-8.5 7.1-.9 0-1.7-.1-2.5-.3l-4.6 1.9 1.2-3.4c-1.6-1.3-2.6-3.1-2.6-5.3 0-3.9 3.8-7.1 8.5-7.1s8.5 3.2 8.5 7.1Z" />
      <path d="M10.2 9.1a1.9 1.9 0 0 1 3.7.7c0 1.3-1.8 1.5-1.8 2.7" />
      <path d="M12.1 14.9h.01" />
    </svg>
  ),
  /* A magnifier: the search ran and came back with nothing. */
  search: (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.8-3.8" />
    </svg>
  ),
  /* Ember itself, keeping the person company: nothing has happened here yet,
     so it dozes (sleepy, pinned — drift and pokes belong to the landing toy,
     2026-09-14). Decorative like the glyphs. */
  ember: <EmberMascot face="sleepy" interactive={false} className="section-empty-flame" />,
  /* Two figures, the second half out of frame: nobody has come by yet. */
  people: (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9.6" cy="8.6" r="3.1" />
      <path d="M3.8 19.2c0-2.9 2.6-4.8 5.8-4.8s5.8 1.9 5.8 4.8" />
      <path d="M16.4 6.1a3 3 0 0 1 .6 5.8" />
      <path d="M18.2 14.9c1.4.7 2.3 1.9 2.3 3.6" />
    </svg>
  ),
};

/**
 * The empty state of a section that exists but has nothing in it yet: a quiet
 * icon over one line of copy, on the page's own ground.
 *
 * Deliberately not a card. A card here reads as a row of content — one more
 * item in the list that is supposed to be empty, or a sibling of the real card
 * below it. The icon carries the "this section is here" signal instead, and
 * costs less height than the card it replaced.
 */
export function SectionEmpty({ icon, children, action }: {
  icon: SectionEmptyIcon;
  children: ReactNode;
  /** The way out of the emptiness, when the section offers one. */
  action?: ReactNode;
}): ReactElement {
  return (
    <div className="section-empty">
      <span className={icon === 'ember' ? 'section-empty-icon is-mascot' : 'section-empty-icon'} aria-hidden={icon === 'ember' ? undefined : true}>{ICONS[icon]}</span>
      <p className="section-empty-copy">{children}</p>
      {action && <div className="section-empty-action">{action}</div>}
    </div>
  );
}
